import type { AuditAction, AuditEvent, LocalDateKey, StrictMode, TaskOccurrence } from '@/types';
import { addDaysToDateKey, compareDateKeys, toLocalDateKey } from './dates';
import { createId } from './ids';

export const VERY_STRICT_CONFIRMATION = 'Diese Aufgabe zählt heute als verpasst.';
export const VERY_STRICT_WAIT_SECONDS = 20;
export const VERY_STRICT_MIN_REASON_LENGTH = 20;

export type StrictAction = 'reduce-target' | 'delete' | 'complete' | 'skip' | 'change-template';

export type StrictViolationCode =
  | 'TARGET_REDUCTION_LOCKED'
  | 'DELETE_LOCKED'
  | 'TARGET_NOT_REACHED'
  | 'REASON_REQUIRED'
  | 'REASON_TOO_SHORT'
  | 'WAIT_NOT_FINISHED'
  | 'CONFIRMATION_MISMATCH'
  | 'CHANGE_EFFECTIVE_TOO_EARLY';

export interface StrictViolation {
  code: StrictViolationCode;
  message: string;
}

export interface StrictActionRequest {
  action: StrictAction;
  occurrence: TaskOccurrence;
  now?: Date;
  newTargetValue?: number;
  reason?: string;
  waitStartedAt?: string;
  confirmation?: string;
  effectiveDate?: LocalDateKey;
}

export interface StrictDecision {
  allowed: boolean;
  violations: StrictViolation[];
  earliestEffectiveDate?: LocalDateKey;
}

function violation(code: StrictViolationCode, message: string): StrictViolation {
  return { code, message };
}

export function earliestStrictChangeDate(today: LocalDateKey): LocalDateKey {
  return addDaysToDateKey(today, 1);
}

export function evaluateStrictAction(request: StrictActionRequest): StrictDecision {
  const { occurrence } = request;
  if (occurrence.strictMode === 'off') return { allowed: true, violations: [] };

  const now = request.now ?? new Date();
  const today = toLocalDateKey(now);
  const isToday = occurrence.date === today;
  const violations: StrictViolation[] = [];
  let earliestEffectiveDate: LocalDateKey | undefined;

  if (
    request.action === 'reduce-target' &&
    isToday &&
    request.newTargetValue !== undefined &&
    request.newTargetValue < (occurrence.targetValue ?? 0) &&
    (!request.effectiveDate ||
      compareDateKeys(request.effectiveDate, earliestStrictChangeDate(today)) < 0)
  ) {
    violations.push(
      violation(
        'TARGET_REDUCTION_LOCKED',
        'Das heutige Ziel kann im strikten Modus nicht reduziert werden.'
      )
    );
  }

  if (request.action === 'delete' && isToday) {
    violations.push(
      violation(
        'DELETE_LOCKED',
        'Die heutige Aufgabe kann im strikten Modus nicht gelöscht werden.'
      )
    );
  }

  if (
    request.action === 'complete' &&
    occurrence.priority === 'mandatory' &&
    (occurrence.targetValue ?? 0) > occurrence.currentValue
  ) {
    violations.push(
      violation(
        'TARGET_NOT_REACHED',
        'Eine Pflichtaufgabe kann erst nach Erreichen ihres Ziels abgeschlossen werden.'
      )
    );
  }

  if (request.action === 'skip') {
    const reasonLength = request.reason?.trim().length ?? 0;
    if (reasonLength === 0) {
      violations.push(
        violation('REASON_REQUIRED', 'Zum Überspringen ist eine Begründung erforderlich.')
      );
    }

    if (occurrence.strictMode === 'very-strict') {
      if (reasonLength > 0 && reasonLength < VERY_STRICT_MIN_REASON_LENGTH) {
        violations.push(
          violation(
            'REASON_TOO_SHORT',
            `Die Begründung muss mindestens ${VERY_STRICT_MIN_REASON_LENGTH} Zeichen lang sein.`
          )
        );
      }
      const waitStarted = request.waitStartedAt
        ? new Date(request.waitStartedAt).getTime()
        : Number.NaN;
      if (
        !Number.isFinite(waitStarted) ||
        now.getTime() - waitStarted < VERY_STRICT_WAIT_SECONDS * 1000
      ) {
        violations.push(
          violation(
            'WAIT_NOT_FINISHED',
            `Die Wartezeit von ${VERY_STRICT_WAIT_SECONDS} Sekunden ist noch nicht vorbei.`
          )
        );
      }
      if (request.confirmation !== VERY_STRICT_CONFIRMATION) {
        violations.push(
          violation('CONFIRMATION_MISMATCH', `Bestätige exakt: „${VERY_STRICT_CONFIRMATION}“`)
        );
      }
    }
  }

  if (request.action === 'change-template' || request.action === 'reduce-target') {
    earliestEffectiveDate = earliestStrictChangeDate(today);
    if (
      request.effectiveDate !== undefined &&
      compareDateKeys(request.effectiveDate, earliestEffectiveDate) < 0
    ) {
      violations.push(
        violation(
          'CHANGE_EFFECTIVE_TOO_EARLY',
          'Die Änderung darf frühestens ab dem nächsten Tag gelten.'
        )
      );
    }
  }

  return {
    allowed: violations.length === 0,
    violations,
    ...(earliestEffectiveDate ? { earliestEffectiveDate } : {})
  };
}

export interface AuditEventInput {
  entityType: string;
  entityId: string;
  action: AuditAction;
  date: LocalDateKey;
  strictMode?: StrictMode;
  reason?: string;
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
  at?: string;
}

export function buildAuditEvent(input: AuditEventInput): AuditEvent {
  const at = input.at ?? new Date().toISOString();
  return {
    id: createId(),
    entityType: input.entityType,
    entityId: input.entityId,
    action: input.action,
    date: input.date,
    createdAt: at,
    updatedAt: at,
    ...(input.strictMode ? { strictMode: input.strictMode } : {}),
    ...(input.reason ? { reason: input.reason } : {}),
    ...(input.before ? { before: input.before } : {}),
    ...(input.after ? { after: input.after } : {})
  };
}
